import { Box } from '@mantine/core'
import { useRef, useState } from 'react'

import useHorizontalOverflow from '@/components/Swiper/useHorizontalOverflow'
import { useIsResponsiveMobileView } from '@/hooks/responsive'

import CarouselController from './CarouselController'
import CarouselSlides from './CarouselSlides'

interface CarsoulSwiperProps<T> {
  title?: string
  swiperList: T[]
  renderSwiper: (item: T, index: number) => React.ReactNode
  slideGap?: string | number
  includeGapInSize?: boolean
}

export default function Swiper<T>({
  title = '',
  swiperList,
  renderSwiper,
  slideGap,
  includeGapInSize,
}: CarsoulSwiperProps<T>) {
  const totalSlides = swiperList.length
  const [activeIndex, setActiveIndex] = useState(0)
  const emblaRef = useRef<any>(null)
  const isMobile = useIsResponsiveMobileView()

  // Use the custom hook for overflow detection
  const { containerRef, hasOverflow } = useHorizontalOverflow([totalSlides])

  const DEFAULT_SLIDES_COUNT = isMobile ? 1 : 3
  const isPrevDisabled = activeIndex === 0
  const isNextDisabled = activeIndex === totalSlides - DEFAULT_SLIDES_COUNT

  const handlePrev = () => {
    if (activeIndex > 0) {
      const newIndex = activeIndex - 1
      setActiveIndex(newIndex)
      emblaRef.current?.scrollTo(newIndex)
    }
  }

  const handleNext = () => {
    if (activeIndex < totalSlides - 1) {
      const newIndex = activeIndex + 1
      setActiveIndex(newIndex)
      emblaRef.current?.scrollTo(newIndex)
    }
  }

  return (
    <Box ref={containerRef} className="swiperContainer" style={{ overflow: 'visible' }}>
      <CarouselController
        title={title}
        isPrevDisabled={isPrevDisabled}
        isNextDisabled={isNextDisabled}
        onPrev={handlePrev}
        onNext={handleNext}
        controllerVisible={hasOverflow}
      />

      <CarouselSlides
        swiperList={swiperList}
        renderSwiper={renderSwiper}
        onSlideChange={setActiveIndex}
        slideGap={slideGap}
        includeGapInSize={includeGapInSize}
        getEmblaApi={embla => {
          emblaRef.current = embla
        }}
      />
    </Box>
  )
}
